/**
 * Company media library — pick previously uploaded files instead of uploading
 * the same app screenshots / logos again. Backed by a per-company ImageKit
 * folder (`<area>/<companyUid>`, appended server-side), so nothing from
 * another workspace can appear here.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import toast from 'react-hot-toast';
import { Check, Film, FileText, Music, ImagePlus, RefreshCw, Search, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { LibraryFile, uploadsService } from '@/services/uploads.service';

export interface MediaLibraryModalProps {
  open: boolean;
  onClose: () => void;
  /** Library area, e.g. MEDIA_FOLDERS.googleAppPromotion. */
  folder: string;
  type?: 'image' | 'video' | 'document' | 'audio' | 'all';
  multiple?: boolean;
  title?: string;
  /** Called with the chosen files; return an error string to reject one (e.g. wrong aspect ratio). */
  validate?: (f: LibraryFile) => string | null;
  onPick: (files: LibraryFile[]) => void;
}

const fmtSize = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function MediaLibraryModal({ open, onClose, folder, type = 'image', multiple = true, title, validate, onPick }: MediaLibraryModalProps) {
  const [files, setFiles] = useState<LibraryFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setFiles(null); setError(null);
    try { setFiles(await uploadsService.library(folder, type)); }
    catch (e: any) { setFiles([]); setError(e?.response?.data?.message ?? 'Could not load the library'); }
  };
  useEffect(() => { if (open) { load(); setSelected(new Set()); setQ(''); } }, [open, folder, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (files ?? []).filter((f) => !n || f.name.toLowerCase().includes(n) || (f.tags ?? []).some((t) => t.toLowerCase().includes(n)));
  }, [files, q]);

  const toggle = (f: LibraryFile) => {
    const reason = validate?.(f);
    if (reason) { toast.error(`${f.name}: ${reason}`); return; }
    setSelected((cur) => {
      const next = new Set(multiple ? cur : []);
      if (next.has(f.fileId)) next.delete(f.fileId); else next.add(f.fileId);
      return next;
    });
  };

  const upload = async (list: FileList | null) => {
    if (!list?.length) return;
    setUploading(true);
    try {
      const added: LibraryFile[] = [];
      for (const file of Array.from(list)) {
        const r = await uploadsService.imagekit(file, folder);
        added.push({
          fileId: r.fileId, name: r.name, url: r.url, thumbnailUrl: r.thumbnailUrl, filePath: '',
          fileType: r.fileType === 'image' ? 'image' : 'non-image', size: r.size, width: r.width, height: r.height, createdAt: new Date().toISOString(),
        });
      }
      setFiles((cur) => [...added, ...(cur ?? [])]);
      setSelected((cur) => new Set([...(multiple ? Array.from(cur) : []), ...added.map((a) => a.fileId)]));
      toast.success(added.length === 1 ? 'Uploaded to your library' : `${added.length} files uploaded to your library`);
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Upload failed');
    } finally { setUploading(false); }
  };

  const remove = async (f: LibraryFile) => {
    if (!window.confirm(`Delete "${f.name}" from your library? Existing CRM previews using this file will no longer display it.`)) return;
    setDeleting(f.fileId);
    try {
      const result = await uploadsService.deleteLibraryFile(f.fileId);
      if (!result.ok) throw new Error('The file could not be deleted. Please try again.');
      setFiles((cur) => (cur ?? []).filter((x) => x.fileId !== f.fileId));
      setSelected((cur) => { const n = new Set(cur); n.delete(f.fileId); return n; });
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Could not delete');
    } finally { setDeleting(null); }
  };

  const confirm = () => {
    const picked = (files ?? []).filter((f) => selected.has(f.fileId));
    if (!picked.length) return;
    onPick(picked);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={title ?? `Your ${type === 'all' ? 'media' : type} library`} widthClass="max-w-3xl">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by file name or tag…"
            className="h-9 w-full rounded-lg border border-border bg-surface pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>
        <Button variant="secondary" size="sm" iconLeft={<ImagePlus size={13} />} loading={uploading} onClick={() => inputRef.current?.click()}>Upload new</Button>
        <input ref={inputRef} type="file" multiple className="hidden" accept={type === 'video' ? 'video/*' : type === 'image' ? (folder.startsWith('whatsapp/') ? 'image/png,image/jpeg' : 'image/png,image/jpeg,image/webp') : type === 'audio' ? 'audio/*' : type === 'document' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv' : 'image/*,video/*'}
          onChange={(e) => { upload(e.target.files); e.currentTarget.value = ''; }} />
        <button type="button" onClick={load} className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label="Reload library"><RefreshCw size={14} /></button>
      </div>
      <p className="mt-1.5 text-2xs text-text-muted">Only files uploaded by your workspace appear here. Click to select{multiple ? ' one or more' : ''}.</p>

      <div className="mt-3 max-h-[52vh] overflow-y-auto">
        {error && <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">{error}</div>}
        {files === null ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">{[...Array(10)].map((_, i) => <div key={i} className="skeleton aspect-square rounded-xl" />)}</div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-xs text-text-muted">
            {files.length === 0 ? 'Nothing here yet — upload once and it stays available for every future campaign.' : 'No files match that search.'}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {shown.map((f) => {
              const on = selected.has(f.fileId);
              const isVideo = f.fileType !== 'image';
              return (
                <div key={f.fileId} className={clsx('group relative aspect-square overflow-hidden rounded-xl border bg-surface-alt transition-all', on ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-primary/50')}>
                  <button type="button" onClick={() => toggle(f)} className="block h-full w-full" aria-pressed={on} aria-label={f.name}>
                    {isVideo
                      ? (f.thumbnailUrl ? <img src={f.thumbnailUrl} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-text-muted">{type === 'document' ? <FileText size={22} /> : type === 'audio' ? <Music size={22} /> : <Film size={22} />}</span>)
                      : <img src={f.thumbnailUrl ?? f.url} alt="" loading="lazy" className="h-full w-full object-cover" />}
                  </button>
                  {on && <span className="pointer-events-none absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-on-primary"><Check size={12} /></span>}
                  <span className="pointer-events-none absolute bottom-0 left-0 right-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4 text-[10px] text-white">
                    {f.name}<br />{f.width && f.height ? `${f.width}×${f.height} · ` : ''}{fmtSize(f.size)}
                  </span>
                  <button type="button" onClick={() => remove(f)} disabled={deleting === f.fileId} aria-label={`Delete ${f.name}`} title="Delete from library"
                    className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-danger group-hover:flex">
                    <Trash2 size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
        <span className="text-2xs text-text-muted">{selected.size ? `${selected.size} selected` : files ? `${files.length} file${files.length === 1 ? '' : 's'} in your library` : ''}</span>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={selected.size === 0}>{selected.size > 1 ? `Use ${selected.size} files` : 'Use selected'}</Button>
        </div>
      </div>
    </Modal>
  );
}
